import React, { useState } from "react";
import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { Box, Chip, Typography } from "@mui/material";
import { MimshakService } from "../../../services/mimshakService";
import { PensionFeeExpenseType } from "../../../interfaces/DTO/mislaka_xml_pension_models";
import { formatCurrency, formatPercentage } from "../../../utils/namesFormater";
import StyledDataGrid from "../../../shared/StyledDataGrid";
import { PolicyOrAccount, Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { FundDataBasic } from "../../../interfaces/DTO/FundDataBasic";
import { adjustedProductTypeColumn, currencyColumn, percentageColumn, percentileColorColumn, productTypeColumn } from "../../../shared/UIParts/columnsFormatter";
import InsuranceProductDrawer from "../Insurance/InsuranceProductDrawer";

interface InvestmentPathTableProps {
  products: Product[];
  fundData?: FundDataBasic[];
}

const InvestmentPathTable: React.FC<InvestmentPathTableProps> = ({
  products,
  fundData,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  // עיבוד הנתונים לטבלה: שורה לכל מסלול השקעה (`InvestmentPathDetail`), כולל מספר ושם פוליסה
  const rows = products.flatMap((product, productIndex) =>
    product.policies_or_accounts.flatMap((policy, policyIndex) => {
      const mergedPaths = MimshakService.mergeDuplicateInvestmentPaths(
        policy.investment_paths
      );

      return mergedPaths.map((path, pathIndex) => {
        const latestSavingFee = MimshakService.getLastFeesByExpenseType(
          policy.management_fee_details,
          PensionFeeExpenseType.FROM_SAVINGS
        );

        const latestDepositFee = MimshakService.getLastFeesByExpenseType(
          policy.management_fee_details,
          PensionFeeExpenseType.FROM_DEPOSITS
        );

        const fund: FundDataBasic | undefined = fundData?.find(
          (f) => f.fund_id === path.path_code
        );

        return {
          id: `${productIndex}-${policyIndex}-${pathIndex}`,
          policy_number: policy.policy_number,
          product_type:product.details.product_type,
          program_name: policy.program_name || "לא זמין",
          path_code: path.path_code,
          path_name: path.path_name,
          accumulated_amount: path.accumulated_amount,
          net_return: path.net_return,
          year_to_date_return: fund?.year_to_date_yield,
          management_fee_savings: latestSavingFee?.management_fee_rate ?? null,
          management_fee_deposits: latestDepositFee?.management_fee_rate ?? null,
          monthly_yield: fund?.monthly_yield,
          yield_3_years: fund?.yield_trailing_3_yrs,
          yield_5_years: fund?.yield_trailing_5_yrs,
          cumulative_6m_yield: fund?.cumulative_6m_yield,
          percentile_6m_yield: fund?.percentile_6m_yield,
          cumulative_12m_yield: fund?.cumulative_12m_yield,
          exposure_stocks: fund?.stock_market_percent,
          exposure_forex: fund?.foreign_currency_exposure_percent,
          exposure_abroad: fund?.foreign_exposure_percent,
          standard_deviation: fund?.standard_deviation,
          alpha: fund?.alpha,
          sharpe_ratio: fund?.sharpe_ratio,
          specialization: fund?.specialization,
          sub_specialization: fund?.sub_specialization,
          fund_name: fund?.fund_name,
          adjusted_product_type: product.details?.adjusted_product_type ?? null,
          deposit_percentage: path.deposit_percentage==0?null:path.deposit_percentage,
        };
      });
    })
  );
  

  // הגדרת עמודות הטבלה
  const columns: GridColDef[] = [
    { field: "policy_number", headerName: "מספר פוליסה", width: 120 },
    {
      field: "program_name",
      headerName: "שם תוכנית",
      width: 300,
      renderCell: (params: any) => {
        const { value, row } = params;
        const chips = [];
    
        if (row.specialization) {
          chips.push(
            <Chip
              key="spec"
              label={row.specialization}
              size="small"
              sx={{ mr: 0.5 }}
            />
          );
        }
    
        if (row.sub_specialization) {
          chips.push(
            <Chip
              key="subspec"
              label={row.sub_specialization}
              size="small"
              color="secondary"
              sx={{ mr: 0.5 }}
            />
          );
        }
    
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap',gap: 0.5, }}>
          {chips}
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value}
          </span>
        </Box>
        );
      },
    },
    adjustedProductTypeColumn(),
    { field: "path_code", headerName: "קוד מסלול", width: 100 },
    percentileColorColumn("percentile_6m_yield", "דירוג 6M"),
    { field: "fund_name", headerName: "שם מסלול", width: 300 },
    // { field: "path_name", headerName: "שם מסלול", width: 200 },
  
    currencyColumn("accumulated_amount", "צבירה", 150, true),
   
   
    
    // percentageColumn("net_return", "תשואה נטו", 120, true),
  
    percentageColumn("management_fee_savings", "דמי ניהול מצבירה", ),
    percentageColumn("management_fee_deposits", "דמי ניהול מהפקדה", ),
    percentageColumn("deposit_percentage", "אחוז מההפקדה", 120),
     percentageColumn("monthly_yield", "תשואה חודש אחרון", 120, false),
     percentageColumn("cumulative_6m_yield", "תשואה 6 חודשים", 120),
   percentageColumn("year_to_date_return", "תשואה מתחילת השנה", 120, true),
    percentageColumn("cumulative_12m_yield", "תשואה 12 חודשים", 120, true),
    percentageColumn("yield_3_years", "תשואה ל-3 שנים", 120, true),
    percentageColumn("yield_5_years", "תשואה ל-5 שנים", 120, true),
  
    percentageColumn("exposure_stocks", "חשיפה למניות", ),
    percentageColumn("exposure_forex", "חשיפה למט״ח", ),
    percentageColumn("exposure_abroad", "חשיפה לחו״ל",),
  
    percentageColumn("standard_deviation", "סטיית תקן", ),
    percentageColumn("alpha", "אלפא", ),
    percentageColumn("sharpe_ratio", "יחס שארפ", ),
  ];
  
  const handleRowClick = (params: GridRowParams) => {
    const match = products.find((product) =>
      product.policies_or_accounts.some(
        (p) => p.policy_number === params.row.policy_number
      )
    );
    if (match) {
      setSelectedProduct(match);
    }
  };


  const handleCloseDrawer = () => {
    setSelectedProduct(null);
  };
  
  
  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" gutterBottom>
        {" "}
        נתוני מסלולי השקעה
      </Typography>
      <StyledDataGrid
        rows={rows}
        columns={columns}
        maxHeight="700px"
        onRowClick={handleRowClick}
        pageSizeOptions={[10, 25, 50]}
      />
       {selectedProduct && (
        <InsuranceProductDrawer
          product={selectedProduct}
          onClose={handleCloseDrawer}
        />
      )}
    </Box>
  );
};

export default InvestmentPathTable;
